import { useMemo, useState, type ReactNode } from "react";
import * as XLSX from "xlsx";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { descarcaFisierText, formatNumar } from "@/utils/targetareFormatting";
import {
  aplicaFiltre,
  citesteRapoarte,
  coloanaMeta,
  comparaValori,
  esteRaportImplicit,
  idFiltru,
  scrieRapoarte,
  type CautareRaport,
  type DefinitieRaport,
  type FiltruRaport,
  type RaportSalvat,
  type SortareRaport,
} from "@/utils/targetareRaport";
import {
  csvDinRanduri,
  randuriPentruExport,
  type ColoanaTabel,
  type RandRaport,
} from "@/utils/targetareTabel";
import { BaraRaport } from "./BaraRaport";
import { ChipuriFiltre } from "./ChipuriFiltre";
import { DialogColoane } from "./DialogColoane";
import { DialogFiltru } from "./DialogFiltru";
import { FiltreRapide } from "./FiltreRapide";
import { MeniuAntet } from "./MeniuAntet";
import { Paginare } from "./Paginare";

export type ActiuneSelectie = {
  eticheta: string;
  onClick: (chei: string[]) => void;
};

type RaportInteractivProps<R extends RandRaport> = {
  definitie: DefinitieRaport<R>;
  randuri: R[];
  isLoading: boolean;
  selectat?: string | null;
  onSelect?: (cheie: string, rand: R) => void;
  celula?: (coloana: ColoanaTabel<R>, rand: R) => ReactNode;
  meniuRand?: (rand: R) => ReactNode;
  actiuniSelectie?: ActiuneSelectie[];
  claseRand?: (rand: R) => string | undefined;
};

function celulaImplicita<R extends RandRaport>(
  coloana: ColoanaTabel<R>,
  rand: R
): ReactNode {
  const v = rand[coloana.id];
  if (coloana.tip === "bool") return v ? "da" : "nu";
  if (coloana.tip === "numar") {
    return (
      <span className="tabular-nums">
        {typeof v === "number" ? formatNumar(v) : String(v ?? "")}
      </span>
    );
  }
  if (Array.isArray(v)) {
    const text = v
      .map((x) => coloana.etichete?.[String(x)] ?? String(x))
      .join(", ");
    return (
      <span title={text} className="block truncate">
        {text}
      </span>
    );
  }
  const text =
    v === null || v === undefined
      ? ""
      : (coloana.etichete?.[String(v)] ?? String(v));
  return (
    <span title={text} className="block truncate">
      {text}
    </span>
  );
}

export function RaportInteractiv<R extends RandRaport>({
  definitie,
  randuri,
  isLoading,
  selectat = null,
  onSelect,
  celula,
  meniuRand,
  actiuniSelectie,
  claseRand,
}: RaportInteractivProps<R>) {
  const {
    coloane: toateColoanele,
    rapoarteImplicite,
    cheieRand,
    cheie,
  } = definitie;
  const [rapoarte, setRapoarte] = useState<RaportSalvat[]>(() => [
    ...rapoarteImplicite,
    ...citesteRapoarte(cheie),
  ]);
  const [raportCurent, setRaportCurent] = useState(rapoarte[0].nume);
  const [coloane, setColoane] = useState<string[]>(rapoarte[0].coloane);
  const [filtre, setFiltre] = useState<FiltruRaport[]>(rapoarte[0].filtre);
  const [sortare, setSortare] = useState<SortareRaport[]>(rapoarte[0].sortare);
  const [randuriPePagina, setRanduriPePagina] = useState(
    rapoarte[0].randuriPePagina
  );
  const [cautare, setCautare] = useState<CautareRaport>(rapoarte[0].cautare);
  const [pagina, setPagina] = useState(1);
  const [selectate, setSelectate] = useState<Set<string>>(() => new Set());
  const [dialogColoane, setDialogColoane] = useState(false);
  const [dialogFiltru, setDialogFiltru] = useState<{
    deschis: boolean;
    filtru: FiltruRaport | null;
  }>({ deschis: false, filtru: null });
  const [dialogRaport, setDialogRaport] = useState(false);
  const [numeRaport, setNumeRaport] = useState("");

  const filtrate = useMemo(
    () => aplicaFiltre(toateColoanele, randuri, filtre, cautare, coloane),
    [toateColoanele, randuri, filtre, cautare, coloane]
  );
  const sortate = useMemo(() => {
    if (sortare.length === 0) return filtrate;
    const copie = [...filtrate];
    copie.sort((a, b) => {
      for (const s of sortare) {
        const c = comparaValori(a[s.id], b[s.id]);
        if (c !== 0) return s.desc ? -c : c;
      }
      return 0;
    });
    return copie;
  }, [filtrate, sortare]);
  const ultimaPagina = Math.max(1, Math.ceil(sortate.length / randuriPePagina));
  const paginaCurenta = Math.min(pagina, ultimaPagina);
  const vizibile = sortate.slice(
    (paginaCurenta - 1) * randuriPePagina,
    paginaCurenta * randuriPePagina
  );
  const coloaneMeta = coloane
    .map((id) => coloanaMeta(toateColoanele, id))
    .filter((c): c is ColoanaTabel<R> => !!c);

  const aplicaRaport = (r: RaportSalvat) => {
    setRaportCurent(r.nume);
    setColoane(r.coloane);
    setFiltre(r.filtre);
    setSortare(r.sortare);
    setRanduriPePagina(r.randuriPePagina);
    setCautare(r.cautare);
    setPagina(1);
  };
  const raportDinStare = (nume: string): RaportSalvat => ({
    nume,
    coloane,
    filtre,
    sortare,
    randuriPePagina,
    cautare,
  });
  const salveazaRaport = () => {
    const nume = numeRaport.trim();
    if (!nume || esteRaportImplicit(rapoarteImplicite, nume)) return;
    const personalizate = [
      ...rapoarte.filter(
        (r) => !esteRaportImplicit(rapoarteImplicite, r.nume) && r.nume !== nume
      ),
      raportDinStare(nume),
    ];
    scrieRapoarte(cheie, personalizate);
    setRapoarte([...rapoarteImplicite, ...personalizate]);
    setRaportCurent(nume);
    setNumeRaport("");
    setDialogRaport(false);
  };
  const stergeRaport = () => {
    if (esteRaportImplicit(rapoarteImplicite, raportCurent)) return;
    const personalizate = rapoarte.filter(
      (r) =>
        !esteRaportImplicit(rapoarteImplicite, r.nume) &&
        r.nume !== raportCurent
    );
    scrieRapoarte(cheie, personalizate);
    setRapoarte([...rapoarteImplicite, ...personalizate]);
    aplicaRaport(rapoarteImplicite[0]);
  };

  const sorteaza = (id: string, desc?: boolean) => {
    const curent = sortare[0];
    const urmatorDesc = desc ?? (curent?.id === id ? !curent.desc : false);
    setSortare([{ id, desc: urmatorDesc }]);
    setPagina(1);
  };
  const adaugaFiltru = (f: FiltruRaport) => {
    setFiltre((prev) =>
      prev.some((x) => x.id === f.id)
        ? prev.map((x) => (x.id === f.id ? f : x))
        : [...prev, f]
    );
    setPagina(1);
  };

  const randuriExport = (doarSelectate: boolean) =>
    randuriPentruExport(
      doarSelectate
        ? sortate.filter((r) => selectate.has(cheieRand(r)))
        : sortate,
      coloaneMeta
    );
  const descarca = (format: "csv" | "xlsx", doarSelectate: boolean) => {
    const zi = new Date().toISOString().slice(0, 10);
    const date = randuriExport(doarSelectate);
    if (format === "csv") {
      descarcaFisierText(
        csvDinRanduri(
          date,
          coloaneMeta.map((c) => c.eticheta)
        ),
        `${definitie.numeFisier}-${zi}.csv`
      );
      return;
    }
    const carte = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      carte,
      XLSX.utils.json_to_sheet(date),
      definitie.numeFisier.slice(0, 30)
    );
    XLSX.writeFile(carte, `${definitie.numeFisier}-${zi}.xlsx`);
  };

  const comutaSelectie = (k: string) =>
    setSelectate((prev) => {
      const set = new Set(prev);
      if (set.has(k)) set.delete(k);
      else set.add(k);
      return set;
    });
  const toatePaginaSelectate =
    vizibile.length > 0 && vizibile.every((r) => selectate.has(cheieRand(r)));
  const comutaPagina = () =>
    setSelectate((prev) => {
      const set = new Set(prev);
      if (toatePaginaSelectate)
        vizibile.forEach((r) => set.delete(cheieRand(r)));
      else vizibile.forEach((r) => set.add(cheieRand(r)));
      return set;
    });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-9" />
        ))}
      </div>
    );
  }

  const randare = (coloana: ColoanaTabel<R>, rand: R): ReactNode =>
    celula
      ? (celula(coloana, rand) ?? celulaImplicita(coloana, rand))
      : celulaImplicita(coloana, rand);

  return (
    <div className="rounded-lg border bg-card">
      <BaraRaport
        coloane={toateColoanele}
        cautare={cautare}
        onCautare={(c) => {
          setCautare(c);
          setPagina(1);
        }}
        rapoarte={rapoarte}
        raportCurent={raportCurent}
        onRaport={(nume) => {
          const r = rapoarte.find((x) => x.nume === nume);
          if (r) aplicaRaport(r);
        }}
        onReset={() => {
          const r =
            rapoarte.find((x) => x.nume === raportCurent) ??
            rapoarteImplicite[0];
          aplicaRaport(r);
        }}
        actiuni={{
          randuriPePagina,
          poateSterge: !esteRaportImplicit(rapoarteImplicite, raportCurent),
          areSelectie: selectate.size > 0,
          onColoane: () => setDialogColoane(true),
          onFiltru: () => setDialogFiltru({ deschis: true, filtru: null }),
          onRanduriPePagina: (n) => {
            setRanduriPePagina(n);
            setPagina(1);
          },
          onSalveaza: () => {
            setNumeRaport(
              esteRaportImplicit(rapoarteImplicite, raportCurent)
                ? ""
                : raportCurent
            );
            setDialogRaport(true);
          },
          onSterge: stergeRaport,
          onDescarca: descarca,
          actiuniSelectie: (actiuniSelectie ?? []).map((a) => ({
            eticheta: a.eticheta,
            onClick: () => a.onClick([...selectate]),
          })),
        }}
      />
      {definitie.filtreRapide && definitie.filtreRapide.length > 0 && (
        <FiltreRapide
          definitii={definitie.filtreRapide}
          randuri={randuri}
          filtre={filtre}
          onFiltre={(f) => {
            setFiltre(f);
            setPagina(1);
          }}
        />
      )}
      <ChipuriFiltre
        coloane={toateColoanele}
        filtre={filtre}
        onComuta={(id, activ) => {
          setFiltre((prev) =>
            prev.map((f) => (f.id === id ? { ...f, activ } : f))
          );
          setPagina(1);
        }}
        onEditeaza={(f) => setDialogFiltru({ deschis: true, filtru: f })}
        onSterge={(id) => {
          setFiltre((prev) => prev.filter((f) => f.id !== id));
          setPagina(1);
        }}
      />
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-muted/60 text-left text-xs font-semibold text-foreground">
              <th className="w-8 border-b border-r px-2 py-2">
                <Checkbox
                  checked={toatePaginaSelectate}
                  onCheckedChange={comutaPagina}
                  aria-label="Selectează rândurile paginii"
                />
              </th>
              {meniuRand && (
                <th
                  className="w-8 border-b border-r px-1 py-2"
                  aria-label="Acțiuni"
                />
              )}
              {coloaneMeta.map((c) => {
                const s = sortare.find((x) => x.id === c.id);
                return (
                  <th
                    key={c.id}
                    className="border-b border-r px-2 py-2 align-middle"
                    style={{ minWidth: c.latime }}
                  >
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="flex items-center gap-1 truncate text-left hover:text-ds-blue-700"
                        onClick={() => sorteaza(c.id)}
                        title={
                          c.sursa
                            ? `${c.eticheta} · sursă: ${c.sursa}`
                            : `Sortează după ${c.eticheta}`
                        }
                      >
                        {c.eticheta}
                        {s &&
                          (s.desc ? (
                            <ArrowDown className="h-3 w-3" />
                          ) : (
                            <ArrowUp className="h-3 w-3" />
                          ))}
                      </button>
                      <MeniuAntet
                        coloane={toateColoanele}
                        coloana={c}
                        randuri={filtrate}
                        onSorteaza={(id, desc) => sorteaza(id, desc)}
                        onAscunde={(id) =>
                          setColoane((prev) => prev.filter((x) => x !== id))
                        }
                        onFiltruValoare={(id, v) =>
                          adaugaFiltru({
                            id: idFiltru(),
                            coloana: id,
                            operator: "egal",
                            valoare: v,
                            activ: true,
                          })
                        }
                      />
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {vizibile.length === 0 ? (
              <tr>
                <td
                  colSpan={coloaneMeta.length + (meniuRand ? 2 : 1)}
                  className="p-8 text-center text-muted-foreground"
                >
                  {definitie.mesajGol ?? "Niciun rând nu corespunde filtrelor."}
                </td>
              </tr>
            ) : (
              vizibile.map((r) => {
                const k = cheieRand(r);
                const esteSelectat = selectate.has(k);
                return (
                  <tr
                    key={k}
                    className={cn(
                      "border-b hover:bg-muted/40",
                      onSelect && "cursor-pointer",
                      (esteSelectat || k === selectat) && "bg-ds-blue-50",
                      claseRand?.(r)
                    )}
                    onClick={() => onSelect?.(k, r)}
                  >
                    <td
                      className="border-r px-2 py-1.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={esteSelectat}
                        onCheckedChange={() => comutaSelectie(k)}
                        aria-label={`Selectează ${k}`}
                      />
                    </td>
                    {meniuRand && (
                      <td
                        className="border-r px-1 py-1.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {meniuRand(r)}
                      </td>
                    )}
                    {coloaneMeta.map((c) => (
                      <td
                        key={c.id}
                        className="max-w-[320px] border-r px-2 py-1.5 align-middle"
                        style={{ minWidth: c.latime }}
                      >
                        {randare(c, r)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Paginare
        pagina={paginaCurenta}
        randuriPePagina={randuriPePagina}
        total={sortate.length}
        selectate={selectate.size}
        onPagina={setPagina}
      />

      {dialogColoane && (
        <DialogColoane
          deschis
          toateColoanele={toateColoanele}
          coloane={coloane}
          onInchide={() => setDialogColoane(false)}
          onAplica={(c) => {
            setColoane(c);
            setDialogColoane(false);
          }}
        />
      )}
      {dialogFiltru.deschis && (
        <DialogFiltru
          deschis
          coloane={toateColoanele}
          initial={dialogFiltru.filtru}
          randuri={randuri}
          onInchide={() => setDialogFiltru({ deschis: false, filtru: null })}
          onAplica={(f) => {
            adaugaFiltru(f);
            setDialogFiltru({ deschis: false, filtru: null });
          }}
        />
      )}
      <Dialog open={dialogRaport} onOpenChange={setDialogRaport}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Salvează raportul
            </DialogTitle>
          </DialogHeader>
          <Input
            value={numeRaport}
            onChange={(e) => setNumeRaport(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") salveazaRaport();
            }}
            placeholder="ex. Energie · mari · fără email"
            aria-label="Nume raport"
          />
          <p className="text-xs text-muted-foreground">
            Se salvează coloanele, ordinea lor, filtrele, sortarea, căutarea și
            numărul de rânduri pe pagină.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogRaport(false)}>
              Anulează
            </Button>
            <Button
              onClick={salveazaRaport}
              disabled={
                !numeRaport.trim() ||
                esteRaportImplicit(rapoarteImplicite, numeRaport.trim())
              }
            >
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
